import { t } from 'i18next';
import { setIsInitSuccess, setMessage } from '../../store/reducers/appSlice';
import { applyPlanState, setAccessToken } from '../../store/reducers/authSlice';
import store from '../../store/store';
import { tokenRevokeAtLogOutUser } from './logout-user';
import { initResponseStateUpdate } from './state-updates';
import { submitLogToBugsnag } from './log';
import {
  APP_ENV,
  INIT_API,
  INTERCOM_ENABLED_ENVS,
} from '../constants/central-api';
import isNotEmptyString from '../helper-functions/isNotEmptyString';
import { runCacheBuster } from '../cache-buster/cache-bust-handler';
import moment from 'moment';
import localStorageHardReset from '../helper-functions/localStorageHardReset';
import { setPendingLoginError } from '../../onboardingFlow/login/pendingLoginError';
import {
  setIsPaymentPopupOpen,
  setIsSwitchShopPopupOpen,
} from '../../store/reducers/uiSlice';
import { clearStats } from '../../store/reducers/userSlice';
import { setHasFetchedTaraChatHistory } from '../../store/reducers/chatSlice';

/** Shown when seller-init refuses the session (403): the seller belongs to
 *  another merchant than this web app. */
export const INIT_FORBIDDEN_MESSAGE =
  "This account can't be opened in this app. Sign in again or contact support.";

/** Why the last `fetchAppInit` call failed; `status` is the HTTP status, or
 *  null when the request itself failed (network, bad JSON). */
export interface AppInitFailure {
  status: number | null;
}

let lastAppInitFailure: AppInitFailure | null = null;

/** The failure of the most recent `fetchAppInit` call, or null when it
 *  succeeded, was skipped, or has not run. */
export function getLastAppInitFailure(): AppInitFailure | null {
  return lastAppInitFailure;
}

export async function fetchAppInit(isSwitchingShop: boolean = false) {
  lastAppInitFailure = null;
  if (!(await runCacheBuster())) {
    const isWhiteLabel = store.getState().UI?.isWhiteLabel;
    try {
      //* Extracting cacheBustToken from URL if there is any
      const queryParameters = new URLSearchParams(window.location.search);
      const cacheBustToken = queryParameters.get(
        'cacheBustToken'
      ) as unknown as string;

      const accessToken = store.getState().auth.authorization.accessToken;
      const requestHeaders: { [idx: string]: any } = {};

      let token: string;
      if (isWhiteLabel) {
        token = accessToken?.currentWhiteLabelSellerToken;
      } else {
        token = accessToken?.sellerToken;
      }

      // Hotfix 2026-08-06: invite-only employee sessions hold no seller JWT.
      // seller-init without one always comes back unsuccessful, and the
      // failure path below logs the user out with "Session expired" — which
      // turned every brand switch (SwitchShop / BrandQuickSwitch /
      // ClientsList) into a logout for invited employees. Their shop list is
      // hydrated from /api/v1/me instead (see App.tsx), so skip the call.
      if (
        !isWhiteLabel &&
        !isNotEmptyString(token) &&
        !isNotEmptyString(cacheBustToken) &&
        isNotEmptyString(store.getState().auth.agency?.accessToken)
      ) {
        return null;
      }

      store.dispatch(setIsInitSuccess(false));
      store.dispatch(setIsPaymentPopupOpen(false));
      store.dispatch(setIsSwitchShopPopupOpen(false));
      store.dispatch(setHasFetchedTaraChatHistory(false));

      if (isNotEmptyString(token)) {
        requestHeaders['Authorization'] = accessToken.type + ' ' + token;
      } else if (isNotEmptyString(cacheBustToken)) {
        requestHeaders['Authorization'] =
          accessToken.type + ' ' + cacheBustToken;
      }
      const response = await fetch(INIT_API, {
        method: 'GET',
        headers: requestHeaders,
      });

      const res = await response.json();
      const { success, data, message } = res;

      if (
        !isWhiteLabel &&
        (isNotEmptyString(token) || isNotEmptyString(cacheBustToken))
      ) {
        store.dispatch(
          setAccessToken({
            ...accessToken,
            sellerToken: isNotEmptyString(token) ? token : cacheBustToken,
            expiresOn: moment().add(21, 'days'),
          })
        );
      }

      //* Removing cacheBustToken from browser URL — only when one was there.
      //  This runs after an await, and the app may have navigated meanwhile
      //  (Login → /oauth/authorize?… is one such path). Rewriting the URL from
      //  the query captured BEFORE the request stripped the current route's
      //  own parameters, so edit the live URL and leave it alone otherwise.
      if (isNotEmptyString(cacheBustToken)) {
        const current = new URL(window.location.href);
        current.searchParams.delete('cacheBustToken');
        window.history.replaceState({}, '', current.toString());
      }

      //* Clear stats to reset the AI Optimizations status card
      if (isSwitchingShop) {
        store.dispatch(clearStats());
      }

      if (success) {
        // Plan state of a signed-in seller's organisation (absent for
        // white-label sellers and tokenless calls). Applied before the splash
        // clears, so a locked account never sees a product page first.
        store.dispatch(applyPlanState(data));
        store.dispatch(setIsInitSuccess(true));
        initResponseStateUpdate(data);
        return data;
      } else {
        store.dispatch(setIsInitSuccess(false));
        lastAppInitFailure = { status: response.status };
        if (!isWhiteLabel) {
          // The logout clears the session, so the route guards send the user
          // to /login; the message is set after it so the login page shows it.
          logoutHandler();
          const failureMessage =
            response.status === 403
              ? INIT_FORBIDDEN_MESSAGE
              : message ?? t('SessionExpired');
          setPendingLoginError(failureMessage);
          store.dispatch(
            setMessage({
              message: failureMessage,
              messageType: 'error',
            })
          );
        }
        return null;
      }
    } catch (exception) {
      store.dispatch(setIsInitSuccess(false));
      lastAppInitFailure = { status: null };
      submitLogToBugsnag('error', `${exception}`);
      if (!isWhiteLabel) {
        logoutHandler();
        setPendingLoginError(t('SessionExpired'));
        store.dispatch(
          setMessage({
            message: t('SessionExpired'),
            messageType: 'error',
          })
        );
      }
      console.log('Error fetching init response', exception);
      submitLogToBugsnag(
        'error',
        `Error fetching init response : ${exception}`
      );
    }
  }
}

const logoutHandler = () => {
  tokenRevokeAtLogOutUser();
  if (INTERCOM_ENABLED_ENVS.includes(APP_ENV)) {
    // @ts-ignore:next-line
    window.Intercom && window.Intercom('shutdown'); //shutdown intercom
  }

  localStorageHardReset();
};
